<template>
  <div class="as-master-page">
    <AsAMasterPageMenu :your_mastered_responsibilities_count="totalRecords" />
    <GuideVideoBanner
      v-if="showGuideBanner && guideData"
      :text="guideData.text"
      :subtext="guideData.subtext"
      :videoUrl="guideData.videoUrl"
      :videoThumbnail="guideData.videoThumbnail"
      @close="handleGuideClose"
    />
    <div
      class="as-master-page-content"
      :class="{ 'as-master-page-content--no-border': selectedResId }"
    >
      <div
        class="as-master-page-banner"
        v-if="responsibilitiesWithNoCheckpoints > 0 || showResponsibilitiesInfoBanner"
      >
        <AsMasterNoCheckpointBanner
          v-if="responsibilitiesWithNoCheckpoints > 0"
          :responsibilityCount="responsibilitiesWithNoCheckpoints"
        />
        <ExGenericBanner
          v-if="showResponsibilitiesInfoBanner"
          role="note"
          title="These are the responsibilities you are an architect of"
          description="To edit them click on their name to drill down into the responsibility card"
          :showAction="false"
          wideGap
        />
      </div>
      <div class="as-master-page-table">
        <ResponsibilityCard v-if="selectedResId" :key="selectedResId" :resId="selectedResId" />
        <MasteredResponsibilityTable
          v-else
          :dataStream="masteredResponsibilities"
          :totalRecords="totalRecords"
          :currentPage="currentPage"
          :pageSize="pageSize"
          :chatterData="ideaMDP450 ? chatterData : []"
          :totalChatterData="ideaMDP450 ? totalChatterData : 0"
          :loading="loading"
          @search="handleSearch"
          @filter="handleFilter"
          @pageChange="handlePageChange"
          @pageSizeChange="handlePageSizeChange"
          @updateArchitectWillCoach="handleUpdateArchitectWillCoach"
          @openChatterDrawer="onOpenChatterDrawer"
          @requestMoreCheckpointsChatter="onRequestMoreChatter"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
import { useRoute } from "vue-router/composables";
import AsAMasterPageMenu from "@components/AsAMasterPageMenu/AsAMasterPageMenu.vue";
import AsMasterNoCheckpointBanner from "@components/AsMaster/AsMasterNoCheckpointBanner/AsMasterNoCheckpointBanner.vue";
import ExGenericBanner from "@components/GenericBanner/ExGenericBanner.vue";
import MasteredResponsibilityTable from "@components/MasteredResponsibilityTable/MasteredResponsibilityTable.vue";
import ResponsibilityCard from "@components/ResponsibilityCard/ResponsibilityCard.vue";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import { useKnockGuides } from "@/composables/common/useKnockGuides";

const props = defineProps({
  dataStream: {
    type: Array,
    default: () => [],
  },
  totalRecords: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  pageSize: {
    type: Number,
    default: 10,
  },
  chatterData: {
    type: Array,
    default: () => [],
  },
  totalChatterData: {
    type: Number,
    default: 0,
  },
  /** MDP-560 — parent-fed fetch state, forwarded to the table to show the shared skeleton. */
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "search",
  "filter",
  "pageChange",
  "pageSizeChange",
  "guideClose",
  "updateArchitectWillCoach",
  "openChatterDrawer",
  "requestMoreCheckpointsChatter",
]);

const instance = getCurrentInstance();
const route = useRoute();
/** Responsibility currently shown in place of the table, driven by ?resId= on this same route. */
const selectedResId = computed(() => route.query.resId || null);
/** As an Architect list chatter (LaunchDarkly idea-MDP-450). */
const ideaMDP450 = computed(() =>
  Boolean(instance?.proxy?.$store?.getters?.["featureFlags/ideaMDP450"])
);

/** Explanatory banner above the table; hidden while a responsibility card replaces the table. */
const showResponsibilitiesInfoBanner = computed(() => ideaMDP450.value && !selectedResId.value);

const onOpenChatterDrawer = (payload) => {
  if (ideaMDP450.value) {
    emit("openChatterDrawer", payload);
  }
};

const onRequestMoreChatter = (payload) => {
  if (ideaMDP450.value) {
    emit("requestMoreCheckpointsChatter", payload);
  }
};

const masteredResponsibilities = ref(props.dataStream);
const showGuideBanner = ref(true);
const guideData = ref(null);

const { selectGuide, subscribeToGuides } = useKnockGuides();
let unsubscribeGuide = null;

const refreshGuide = () => {
  const masterGuide = selectGuide({ key: "as-a-master" });
  const step = masterGuide?.steps?.[0];
  if (step?.content?.text) {
    guideData.value = {
      text: step.content.text,
      subtext: step.content.subText || step.content.subtext || "",
      videoUrl: step.content.videoUrl || "",
      videoThumbnail: step.content.videoThumbnail || "",
    };
  }
};

const initializeKnockGuide = () => {
  unsubscribeGuide = subscribeToGuides(refreshGuide);
  refreshGuide();
};

const handleGuideClose = async () => {
  showGuideBanner.value = false;
  if (guideData.value?.markAsArchived) {
    try {
      await guideData.value.markAsArchived();
    } catch (e) {
      console.error("Error archiving guide:", e);
    }
  }
  emit("guideClose");
};

onMounted(async () => {
  initializeKnockGuide();
});

onBeforeUnmount(() => {
  if (typeof unsubscribeGuide === "function") {
    unsubscribeGuide();
    unsubscribeGuide = null;
  }
});

watch(
  () => props.dataStream,
  (newVal) => {
    masteredResponsibilities.value = newVal;
  }
);

// Calculate responsibilities with no active checkpoints
const responsibilitiesWithNoCheckpoints = computed(() => {
  return masteredResponsibilities.value.filter((resp) => (resp.active_checkpoints_count || 0) === 0)
    .length;
});

const handleSearch = (query) => {
  emit("search", query);
};

const handleFilter = (filters) => {
  emit("filter", filters);
};

const handlePageChange = (page) => {
  emit("pageChange", page);
};

const handlePageSizeChange = (size) => {
  emit("pageSizeChange", size);
};

const handleUpdateArchitectWillCoach = (payload) => {
  emit("updateArchitectWillCoach", payload);
};
</script>

<style scoped>
.as-master-page {
  width: 100%;
  background-color: #ffffff;
}
.as-master-page-content {
  display: flex;
  flex-direction: column;
  gap: 15px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
  background: #ffffff;
  box-shadow:
    0 1px 3px 0 rgba(16, 24, 40, 0.1),
    0 1px 2px 0 rgba(16, 24, 40, 0.06);
}
.as-master-page-content--no-border {
  border: none;
  box-shadow: none;
}
.as-master-page-banner {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 20px 16px 0px 16px;
}
.as-master-page-table {
  margin-top: 15px;
}

::v-deep(.menu-btn) {
  padding: 0px !important;
}

::v-deep(.input-search) {
  right: 16px !important;
}
:deep(.as-a-master-page-menu .row) {
  margin-top: 0px !important;
}
</style>
